import React, { useContext, createContext, useState, useEffect, useRef, useMemo } from "react";

import { useApi } from "../services/api-hook";
import {
	isRelationshipStatusActive,
	isRelationshipStatusPendentOrNegotiation,
	getUserToken,
	redirectNewOffer
} from "../shared/utils";
import jwt_decode from "jwt-decode";
import {
	MULTI_ACCOUNTS_API,
	OFFERS_API,
	BFF_API,
	BFF_MALL_API,
	EMAIL_REDIRECT_POLL_TIMEOUT,
	GOL_FIDELITY_PROGRAM,
	Type2FA
} from "../../../js/shared/constants";
import { MANAGER_LIMIT_AVERAGE_MILES } from "../shared/enums";
import {
	getPricesQuickSale,
	getPricesForwardPayment,
	getOffers,
	send2FAEmailRedirectOption,
	getUnleashEnabledFeature,
	postRequest2FACodeLatam,
	post2FACode
} from "../shared/requests-api";
import { logEvent } from "../shared/firebase";
import { createPollingRequest } from "../../../js/shared/long-polling";

const mockOfferTempData = {
	bank: {},
	data: {},
	form: {
		completedBoxDataBankDetails: undefined,
		completedBoxDataFidelityProgramDetails: undefined,
		completedBoxOfferMiles: false,
		completedBoxCreditCardDetails: false
	}
};

const SCHEDULED_ID = 3;
const CONVENTIONAL_ID = 1;

export const NewOfferContext = createContext();
export const useNewOffer = () => useContext(NewOfferContext);
export const NewOfferProvider = ({ children }) => {
	// Api Instance
	const { requestApi } = useApi();

	// Get data from jwt
	const payload = jwt_decode(getUserToken());

	// Fetch data states
	// banks
	const [banks, setBanks] = useState({ data: [], isFetching: false });

	// Create UUID for new offer and send to Cybersource
	const [uuidCybersource, setUuidCybersource] = useState("");

	// user selected company
	const [currentProgram, setCurrentProgram] = useState({});
	// offer editing
	const [editOffer, setEditOffer] = useState({});
	// other components can force create offer's step
	const [forceStep, setForceStep] = useState(undefined);
	// rank position based on user's price
	const [positionRank, setPositionRank] = useState(undefined);
	// flag to indicate if is smiles diamond
	const [smilesDiamond, setSmilesDiamond] = useState(undefined);
	// flag to indicate if is smiles diamond
	const [smilesRange, setSmilesRange] = useState(undefined);

	const [twoFactorData, setTwoFactorData] = useState({
		alert: undefined,
		button: undefined,
		helpTextBeforeClickButton: undefined,
		helpTextAfterClickButton: undefined,
		modal: {
			title: undefined,
			description: undefined,
			timeLeftInSeconds: 120,
			maxLength: 10
		},
		completionAlert: undefined,
		code: undefined,
		is2FAEnabled: false
	});

	const [hasClickedRequest2FACode, setHasClickedRequest2FACode] = useState(false);
	const [hasTriedToProceedWithout2FA, setHasTriedToProceedWithout2FA] = useState(false);

	// offer data
	const [offerData, setOfferData] = useState({
		data: {
			price: "",
			quant: 0,
			averageMiles: 0,
			reductions: 0,
			averageMilesMax: 0
		},
		isFetching: false
	});
	const [airlineProgramsSettings, setAirlineProgramsSettings] = useState([]);
	const [
		showModalErrorTwoFactorAuthentication,
		setShowModalErrorTwoFactorAuthentication
	] = useState(false);
	// companies
	const [programsFidelity, setProgramsFidelity] = useState({
		data: [],
		isFetching: false,
		isDone: false
	});
	// prices
	const quantMilesRef = useRef(0);
	const averageMilesRef = useRef(0);
	const [pricesData, setPricesData] = useState({ data: {}, isFetching: false });
	// bonus
	const [bonusData, setBonusData] = useState({ data: {}, isFetching: false });
	// user data
	const [userData, setUserData] = useState({
		data: { bankData: {}, loyaltyData: {} },
		isFetching: false
	});
	// credit card data
	const [creditCardData, setCreditCardData] = useState({
		data: {},
		isFetching: false,
		hasCreditCard: false,
		isDone: false
	});
	// statistics sales data
	const [statisticSales, setStatisticSales] = useState({
		data: {},
		isFetching: false
	});
	// bank data
	const [bankData, setBankData] = useState({
		data: {}
	});
	// loyalty data
	const [loyaltyData, setLoyaltyData] = useState({
		data: {}
	});
	// ranking data
	const [rankingData, setRankingData] = useState({
		data: {},
		isFetching: false
	});
	// participant data
	const [participantData, setParticipantData] = useState({
		data: {},
		isFetching: false,
		hasManagementActive: false,
		hasRelationshipPendent: false
	});
	// offers data
	const [offersData, setOffersData] = useState({
		data: { offers: [] },
		isFetching: false,
		isDone: false
	});
	// sending offer (save)
	const [offerSentStatus, setOfferSentStatus] = useState({
		success: false,
		isDone: false,
		isFetching: false
	});

	const [quickSaleProblem, setQuickSaleProblem] = useState(false);

	const isEditOffer = useMemo(() => Object.keys(editOffer).length > 0, [editOffer]);

	const [forwardPaymentProblem, setForwardPaymentProblem] = useState(false);

	// General states MOCK
	const [newOfferTempData, setNewOfferTempData] = useState(mockOfferTempData);

	// Steps validation
	const [stepValidation, handlerStepValidation] = useState(false);

	// 2FA email redirect status
	const [email2FAStatusValidation, setEmail2FAStatusValidation] = useState({
		isValidated: false,
		isFetching: false,
		error: null
	});

	// 2FA status feature - Unleash
	const [status2FAEnabledFeature, setStatus2FAEnabledFeature] = useState(false);

	const cleanNewOfferTempData = () => {
		setNewOfferTempData(mockOfferTempData);
	};

	const getOfferType = forwardPayment => {
		if (forwardPayment) return SCHEDULED_ID;
		else return CONVENTIONAL_ID;
	};

	const clearContext = () => {
		setEditOffer({});
		setUserData({
			data: { bankData: {}, loyaltyData: {} },
			isFetching: false
		});
		setCreditCardData({
			data: {},
			isFetching: false,
			hasCreditCard: false,
			isDone: false
		});
		setParticipantData({
			data: {},
			isFetching: false,
			hasManagementActive: false,
			hasRelationshipPendent: false
		});
		setOffersData({
			data: { offers: [] },
			isFetching: false,
			isDone: false
		});
		setNewOfferTempData(mockOfferTempData);
		handlerStepValidation(false);
	};

	// phone multiplus
	const [phoneMultiplus, setPhoneMultiplus] = useState(undefined);

	// all phones multiplus
	const [allPhonesMultiplus, setAllPhonesMultiplus] = useState([]);

	// accepntace terms
	const [acceptanceTerms, setAcceptanceTerms] = useState(undefined);

	// Call banks data
	const fetchBanksData = async () => {
		let getBanks = requestApi("GET", `${BFF_API}/offer/user/banks`);

		try {
			setBanks({ data: banks.data, isFetching: true });
			const response = await getBanks;
			setBanks({ data: response.data, isFetching: false });
		} catch (e) {
			console.log(e);
			setBanks({ data: banks.data, isFetching: false });
		}
	};

	// Call statistic data
	const fetchStatisticSales = async fidelityId => {
		const { id } = currentProgram;
		let getData = requestApi("GET", `${BFF_API}/offer/statistics/sales/${fidelityId || id}`);

		try {
			setStatisticSales({ data: statisticSales.data, isFetching: true });
			const response = await getData;
			setStatisticSales({ data: response.data, isFetching: false });
		} catch (e) {
			console.log(e);
			setStatisticSales({ data: statisticSales.data, isFetching: false });
		}
	};

	const fetchDataUser = async (
		fidelityId,
		isForwardPayment,
		paymentDeadline,
		paymentScheduleType
	) => {
		// Se for gol, buscar as estatisticas
		fidelityId == 4 && fetchStatisticSales(fidelityId);
		const offerType = getOfferType(isForwardPayment);

		let getFidelityData = requestApi(
			"GET",
			`${BFF_API}/offer/fidelity-data/${fidelityId}?offerType=${offerType}&paymentDeadline=${paymentDeadline}&paymentScheduleType=${paymentScheduleType}`
		);

		try {
			setUserData({ data: userData.data, isFetching: true });
			const response = await getFidelityData;
			setBankData({ data: response.data.bankData });

			if (
				fidelityId === GOL_FIDELITY_PROGRAM &&
				airlineProgramsSettings &&
				status2FAEnabledFeature
			) {
				const golAirlineProgramsSettings =
					airlineProgramsSettings &&
					Object.values(airlineProgramsSettings).find(
						item => item.idAirlinesProgram === GOL_FIDELITY_PROGRAM
					);

				let strategyType;

				golAirlineProgramsSettings?.options.forEach(item => {
					if (item.selected && item?.type) {
						strategyType = item.type;
						setLoyaltyData({
							data: {
								...response.data.loyaltyData,
								user: response.data.loyaltyData.username,
								twoFactorAuthenticationStrategy: item.type
							}
						});
						return;
					}
				});
			} else {
				setLoyaltyData({
					data: { ...response.data.loyaltyData, user: response.data.loyaltyData.username }
				});
			}

			await checkMultiplusOfferPhone(response.data.loyaltyData.phone);

			if ((fidelityId === 3 || fidelityId === 1) && response.data.loyaltyData.isMaxPhone === 1) {
				setPhoneMultiplus(response.data.loyaltyData.phone);
			}

			setUserData({
				isFetching: false
			});
			setAcceptanceTerms(response.data.termsData.userAcceptedTerms);
		} catch (e) {
			console.log(e);
			setBankData({ data: {} });
			setLoyaltyData({ data: {} });
			setUserData({ data: userData.data, isFetching: false });
		}
	};

	// Call programs data
	const fetchProgramsFidelityData = async () => {
		let getProgramsFidelity = requestApi("GET", `${BFF_API}/offer/fidelity`);

		try {
			setProgramsFidelity({ data: programsFidelity.data, isFetching: true, isDone: false });
			const response = await getProgramsFidelity;
			setProgramsFidelity({
				data: [...response.data.nationals, ...response.data.international],
				isFetching: false,
				isDone: true
			});
		} catch (e) {
			console.log(e);
			setProgramsFidelity({ data: programsFidelity.data, isFetching: false, isDone: true });
		}
	};

	const handleAverageBySuggestion = (offerAverageMiles, averageMilesSuggest, countAllowedSales) => {
		let averageMiles =
			!(participantData.hasManagementActive && offerAverageMiles > MANAGER_LIMIT_AVERAGE_MILES) &&
			offerAverageMiles >= 3500 // caso tiver vinculo ativo e a media for maior que limite, então pega a sugestão
				? offerAverageMiles
				: averageMilesSuggest;

		if (!averageMiles) {
			if (!countAllowedSales) {
				averageMiles = 3500;
			}
		}

		return averageMiles;
	};

	// Call prices data
	const fetchPricesData = async (miles, program = currentProgram, average, callback) => {
		quantMilesRef.current = miles;
		averageMilesRef.current = average;
		let getDataPrices = requestApi(
			"GET",
			`${BFF_API}/offer/prices/${program.id}/${miles}${average ? `?average=${average}` : ""}`
		);
		try {
			setPricesData({ ...pricesData, isFetching: true });
			const response = await getDataPrices;
			if (quantMilesRef.current != miles || averageMilesRef.current != average) {
				return;
			}
			setPricesData({
				...pricesData,
				isFetching: false,
				data: {
					...pricesData.data,
					...response.data
				}
			});
			if (callback) {
				callback(response.data);
			}
		} catch (e) {
			console.log(e);
			setPricesData({ ...pricesData, isFetching: false });
		}
	};

	// Call bonus data
	const fetchBonusData = async (program = currentProgram, callback) => {
		const acronym = program.acronym.toLowerCase();

		let getDataBonus = requestApi("GET", `${OFFERS_API}/bonus/${acronym}`);

		try {
			setBonusData({ ...bonusData, isFetching: true });
			const response = await getDataBonus;
			setBonusData({
				...bonusData,
				isFetching: false,
				data: {
					...response.data
				}
			});
			if (callback) {
				callback(response.data);
			}
		} catch (e) {
			console.log(e);
			setBonusData({ ...bonusData, isFetching: false, data: {} });
		}
	};

	// Call ranking data
	const fetchRankingData = async (
		miles,
		program = currentProgram,
		average,
		isForwardPayment,
		callback,
		paymentDeadline = 0,
		paymentScheduleType = undefined
	) => {
		const offerType = getOfferType(isForwardPayment);
		let getDataRanking = requestApi(
			"GET",
			`${BFF_API}/ranking/fidelity/${program.id}/${miles}/prices?offset=50${
				average ? `&average=${average}` : ""
			}${`&offerType=${offerType}`}
			${paymentDeadline ? `&paymentDeadline=${paymentDeadline}` : ""}
			${paymentScheduleType ? `&paymentScheduleType=${paymentScheduleType}` : ""}`
		);
		try {
			setRankingData({ data: rankingData.data, isFetching: true });
			const response = await getDataRanking;
			setRankingData({ data: response.data, isFetching: false });
			setPricesData({
				...pricesData,
				data: {
					...pricesData.data,
					...response.data
				}
			});
			if (callback) {
				callback(response.data);
			}
		} catch (e) {
			console.log(e);
			setRankingData({ data: rankingData.data, isFetching: false });

			if (callback) {
				callback(rankingData.data);
			}
		}
	};

	// Call credit card data
	const fetchCreditCardData = async () => {
		let getDataCreditCard = requestApi("GET", `${BFF_API}/offer/user/creditcard`);

		try {
			setCreditCardData({ data: creditCardData.data, isFetching: true, isDone: false });
			const response = await getDataCreditCard;
			setCreditCardData({
				data: response.data,
				isFetching: false,
				isDone: true,
				hasCreditCard: !!response.data.name
			});
		} catch (e) {
			setCreditCardData({
				data: {},
				isFetching: false,
				isDone: true,
				hasCreditCard: false
			});
		}
	};

	// Call offers data
	const fetchOffersData = async () => {
		let getDataOffers = requestApi("GET", `${OFFERS_API}/offer`);

		try {
			setOffersData({ data: offersData.data, isFetching: true, isDone: false });
			const response = await getDataOffers;
			setOffersData({ data: response.data, isFetching: false, isDone: true });
			setAirlineProgramsSettings(response.data.airlineProgramsSettings);
		} catch (e) {
			console.log(e);
			setOffersData({ data: offersData.data, isFetching: false, isDone: true });
		}
	};

	// Call participant data
	const fetchParticipantData = async () => {
		let getDataParticipant = requestApi("GET", `${MULTI_ACCOUNTS_API}/participant`);

		try {
			setParticipantData({ ...participantData, isFetching: true, isDone: false });
			const response = await getDataParticipant;

			// Valida se é um usuário com gestor com vinculo pra ignorar dados bancários
			let hasManagementActive =
				response.data && response.data.some(re => isRelationshipStatusActive(re));
			// Vinculo pendente
			let hasRelationshipPendent =
				!hasManagementActive && // Se tiver vinculo ativo não precisa validar
				response.data &&
				response.data.some(re => isRelationshipStatusPendentOrNegotiation(re));

			setParticipantData({
				data: response.data,
				hasManagementActive,
				hasRelationshipPendent,
				isFetching: false,
				isDone: true
			});
		} catch (e) {
			console.log(e);
			setParticipantData({ ...participantData, isFetching: false, isDone: true });
		}
	};

	// Call all phones multiplus
	const checkMultiplusOfferPhone = async phone => {
		const checkPhone = requestApi("GET", `${BFF_API}/offer/check-phone/${phone}`);

		try {
			const response = await checkPhone;

			setAllPhonesMultiplus(response.data);
		} catch (e) {
			setAllPhonesMultiplus([]);
			//setAllPhonesMultiplus([{ id: 28, phone: "61998543418", enable: "1", isUsedOnWebSite: "1" }]);
			console.error(e);
		}
	};

	// Call phone multiplus
	const getPhoneMultiplus = async () => {
		const getPhone = requestApi("GET", `${OFFERS_API}/phone-mm`);
		try {
			const response = await getPhone;
			setPhoneMultiplus(response.data.phone);
		} catch (e) {
			console.error(e);
		}
	};

	// Call 2FA feature - Unleash
	const get2FAEnabledFeature = async () => {
		try {
			const response = await getUnleashEnabledFeature("oferta-mfa-smiles");
			setStatus2FAEnabledFeature(response.data);
		} catch (e) {
			console.error(e);
		}
	};

	const getOfferData = async fidelityId => {
		try {
			const response = await getOffers(getUserToken(), fidelityId);
			if (response?.data?.offers?.length > 0) {
				return response.data.offers[0];
			}
			return null;
		} catch (e) {
			console.error(e);
		}
	};

	// Edit offer
	const selectOfferToEdit = async (offer, history) => {
		if (!offer || !offer.fidelity) {
			setEditOffer({});
			setCurrentProgram({});
			setForceStep(undefined);
			setNewOfferTempData(mockOfferTempData);
		} else {
			if (history) {
				redirectNewOffer(history, !!offer.id);
			}

			let obj = programsFidelity.data.find(program => program.id === offer.fidelity.id);
			//newQuant e newPrice sao valores de quando o usuário preencheu deslogado na LP
			let quant = offer.newQuant || offer.quant;
			let price = String(offer.newPrice || offer.price).replace(".", ",");
			let isForwardPayment = offer.forwardPayment;
			let paymentDeadline = offer.paymentDeadline;
			let paymentScheduleType = offer.paymentScheduleType;
			setEditOffer(offer);
			setForceStep(2);
			setNewOfferTempData({ ...newOfferTempData, bank: obj });
			setCurrentProgram(obj);
			setSmilesDiamond(Boolean(offer.specialCategory));
			await fetchDataUser(obj.id, isForwardPayment, paymentDeadline, paymentScheduleType);
			fetchBanksData();
			// Caso a oferta esteja com quantidade inválida
			if (quant < offer.fidelity.valMinMiles || quant === 0) {
				setForceStep(3);
				setOfferData({
					...offerData,
					data: {
						quant,
						price: 0,
						averageMiles: 0,
						reductions: 0
					}
				});
			} else {
				await fetchRankingData(
					quant,
					obj,
					offer.averageMiles,
					isForwardPayment,
					async data => {
						let averageMiles = data.averageMilesSuggestion;
						const averageMilesMax = parseInt(data.averageMilesMax) || parseInt(quant);
						let showModalQuickSaleProblem = false;
						let showModalForwardPaymentProblem = false;

						// Verifica se está elegivel para venda rapida
						if (offer.showModalQuickSaleProblem && offer.editable !== false) {
							showModalQuickSaleProblem = true;
						} else if (offer.quickSale) {
							if (!averageMiles && offer.editable !== false) {
								showModalQuickSaleProblem = true;
							} else if (!averageMiles) {
								showModalForwardPaymentProblem = false;
							} else {
								let pricesQuick = await getPricesQuickSale();
								let maxAverageMiles = pricesQuick.data.find(f => f.id_airline_program == obj.id)
									.maxAverageMiles;

								if (averageMiles > maxAverageMiles) {
									showModalQuickSaleProblem = true;
								}
							}
						}

						if (showModalQuickSaleProblem !== quickSaleProblem) {
							setQuickSaleProblem(showModalQuickSaleProblem);
						}

						// Verifica se está elegivel para o pagamento antecipado
						if (offer.showModalForwardPaymentProblem && offer.editable !== false) {
							showModalForwardPaymentProblem = true;
						} else if (offer.forwardPayment) {
							const offerData = await getOfferData(offer.fidelity.id);
							if (offerData && offerData.passengerLimitExceeded) {
								showModalForwardPaymentProblem = false;
							} else if (!averageMiles) {
								showModalForwardPaymentProblem = false;
							} else {
								let pricesForwardPayment = await getPricesForwardPayment();
								let maxAverageMiles = pricesForwardPayment.data.find(
									f => f.id_airline_program == obj.id
								).maxAverageMiles;
								if (averageMiles > maxAverageMiles) {
									showModalForwardPaymentProblem = true;
								}
							}
						}

						if (showModalForwardPaymentProblem !== forwardPaymentProblem) {
							setForwardPaymentProblem(showModalForwardPaymentProblem);
						}

						setForceStep(3);

						await setOfferData({
							...offerData,
							data: {
								quant: Number(quant),
								price: price,
								averageMiles:
									quant < 15000
										? 3500
										: offer.quickSale || offer.forwardPayment
										? 3500
										: handleAverageBySuggestion(
												offer.averageMiles,
												data.averageMilesSuggestion,
												data.countAllowedSales
										  ),
								reductions: !offer.quickSale && !offer.forwardPayment ? offer.reductionsLimit : 0,
								averageMilesMax: averageMilesMax
							}
						});
					},
					paymentDeadline,
					paymentScheduleType
				);
			}
		}
	};

	// Post offer
	const postOfferData = async () => {
		const _price =
			typeof offerData.data.price === "number"
				? offerData.data.price
				: Number(offerData.data.price.replace(",", "."));

		let tempData = {
			averageMiles: offerData.data.quant < 15000 ? 3500 : offerData.data.averageMiles,
			password: loyaltyData.data.password,
			login: loyaltyData.data.user || bankData.data.cpfcnpj || payload.ssn.replace(/[^0-9]/g, ""),
			pwAwardSecondary: loyaltyData.data.pwAwardSecondary,
			phone: !!loyaltyData.data.phone ? loyaltyData.data.phone.replace(/\D/g, "") : null,
			twoFactorAuthenticationStrategy: loyaltyData.data.twoFactorAuthenticationStrategy,
			fidelityId: currentProgram.id,
			price: _price,
			quant: offerData.data.quant,
			quickSale: editOffer.quickSale ? 1 : undefined,
			forwardPayment: editOffer.forwardPayment ? 1 : undefined,
			sessionId: uuidCybersource,
			...(currentProgram.id === 4 ? { reductions: offerData.data.reductions } : {}),
			...(currentProgram.id === 14 && { lastName: loyaltyData.data.lastName }),
			userAcceptedTerms: acceptanceTerms,
			specialCategory: smilesDiamond ? "diamante" : null,
			paymentDeadline: editOffer.paymentDeadline,
			workingDays: editOffer.workingDays,
			paymentScheduleType: editOffer.paymentScheduleType
		};

		let postOffer = requestApi(
			"POST",
			`${BFF_API}/offer/register/${tempData.fidelityId}`,
			tempData
		);

		try {
			setOfferData({ ...offerData, isFetching: true });
			const response = await postOffer;
			setOfferData({ ...offerData, isFetching: false });

			let paramsTracking = {
				fidelity_program_type: currentProgram.international === "1" ? "Internacional" : "Nacional",
				fidelity_select: currentProgram.fidelityName,
				quant_miles: tempData.quant,
				price: tempData.price,
				average_miles: tempData.averageMiles,
				reductions_number: tempData.reductions,
				available_cpfs: rankingData.data.passengersAvailable,
				ranking: positionRank
			};
			logEvent("create_edit_offer", paramsTracking);

			return true;
		} catch (e) {
			console.log(e);
			setOfferData({ ...offerData, isFetching: false });
			return false;
		}
	};

	// Post credit card
	const postCreditCardData = async () => {
		let tempData = {
			cpf: creditCardData.data.cardCpf || payload.ssn.replace(/[^0-9]/g, ""),
			number: String(creditCardData.data.cardNumber),
			securityCode: String(creditCardData.data.cvv),
			name: creditCardData.data.holderName,
			validDateMonth: creditCardData.data.expirationMonth,
			validDateYear: creditCardData.data.expirationYear
		};

		let postCreditCard = requestApi("POST", `${BFF_API}/offer/user/creditcard`, tempData);

		try {
			setOfferData({ ...offerData, isFetching: true });
			const response = await postCreditCard;
			setOfferData({ ...offerData, isFetching: false });

			setCreditCardData({
				...creditCardData,
				hasCreditCard: true
			});
			return true;
		} catch (e) {
			console.log(e);
			setOfferData({ ...offerData, isFetching: false });
			return false;
		}
	};

	// Post bank
	const postBankData = async () => {
		let tempData = {
			cpfcnpj: bankData.data.cpfcnpj || payload.ssn.replace(/[^0-9]/g, ""),
			bankId: Number(bankData.data.bankId),
			type: bankData.data.type,
			agency: bankData.data.agency.split("-")[0],
			dacAgency: bankData.data.agency.split("-")[1] ? bankData.data.agency.split("-")[1] : null,
			account: bankData.data.account.split("-")[0],
			dacAccount: bankData.data.account.split("-")[1] ? bankData.data.account.split("-")[1] : null
		};

		let postBank = requestApi("POST", `${BFF_API}/offer/user/bank`, tempData);

		try {
			setOfferData({ ...offerData, isFetching: true });
			const response = await postBank;
			setOfferData({ ...offerData, isFetching: false });
			return true;
		} catch (e) {
			console.log(e);
			setOfferData({ ...offerData, isFetching: false });
			return false;
		}
	};

	// Post and check 2FA email redirect validation
	const postAndCheck2FAEmailRedirectStatus = async () => {
		setEmail2FAStatusValidation({ ...email2FAStatusValidation, isFetching: true });

		try {
			const authToken = getUserToken();

			await send2FAEmailRedirectOption(authToken);

			const response = await createPollingRequest({
				url: `${BFF_MALL_API}/miles/two-factor-check-email`,
				headers: {},
				authToken,
				timeoutInSeconds: EMAIL_REDIRECT_POLL_TIMEOUT
			});

			setEmail2FAStatusValidation({
				...email2FAStatusValidation,
				isValidated: response.data?.validated,
				isFetching: false,
				error: !response.data?.validated
			});
			setShowModalErrorTwoFactorAuthentication(!response.data?.validated);

			return response.data?.validated;
		} catch (error) {
			console.error("Error while trying to define 2FA option for user!", error);
			return false;
		}
	};

	const request2FACodeLatam = async () => {
		try {
			await postRequest2FACodeLatam({
				password: loyaltyData.data.password,
				login: loyaltyData.data.user,
				sessionId: uuidCybersource,
				methodTwoFactor: "email",
				airlineProgram: currentProgram.id,
				miles: offerData.data.quant,
				price: offerData.data.price
			});

			return true;
		} catch {
			return false;
		}
	};

	const send2FACode = async () => {
		try {
			await post2FACode({
				code: twoFactorData.code,
				sessionId: uuidCybersource,
				airlineProgramId: currentProgram.id
			});

			return true;
		} catch {
			return false;
		}
	};

	const postData = async () => {
		if (
			currentProgram.id === GOL_FIDELITY_PROGRAM &&
			loyaltyData.data.twoFactorAuthenticationStrategy === Type2FA.EMAIL_REDIRECT &&
			!email2FAStatusValidation.isValidated
		) {
			if (!(await postAndCheck2FAEmailRedirectStatus())) {
				return;
			}
		}

		setOfferSentStatus({
			isDone: false,
			isFetching: true
		});

		if (participantData.hasManagementActive) {
			console.log("Skip bank");
		} else {
			console.log("Bank data");
			if (!(await postBankData())) {
				setOfferSentStatus({
					success: false,
					isDone: true,
					isFetching: false
				});
				return;
			}
		}

		if (
			!creditCardData.hasCreditCard &&
			currentProgram &&
			currentProgram.international === "1" &&
			(currentProgram.id == 25 ||
				currentProgram.id == 11 ||
				currentProgram.id == 10 ||
				currentProgram.id == 14)
		) {
			// Precisa salvar cartão
			console.log("Credit card data");
			if (!(await postCreditCardData())) {
				setOfferSentStatus({
					success: false,
					isDone: true,
					isFetching: false
				});
				return;
			}
		}

		let success = await postOfferData();

		setEmail2FAStatusValidation({ ...email2FAStatusValidation, error: null });

		setOfferSentStatus({
			success,
			isDone: true,
			isFetching: false
		});
	};

	// Init fetch
	useEffect(() => {
		fetchProgramsFidelityData();
		fetchParticipantData();
		fetchOffersData();
		fetchCreditCardData();
		getPhoneMultiplus();
		get2FAEnabledFeature();
	}, []);

	// Init fetch
	useEffect(() => {
		if (currentProgram.acronym) {
			fetchBonusData(currentProgram);
		}
	}, [currentProgram]);

	useEffect(() => {
		if (!currentProgram && !airlineProgramsSettings) {
			setTwoFactorData(prevState => ({
				...prevState,
				is2FAEnabled: false
			}));

			return;
		}

		const currentAirlineProgramSettings = Object.values(airlineProgramsSettings).find(
			item => item.idAirlinesProgram === currentProgram.id
		);

		if (currentAirlineProgramSettings) {
			const emailRedirectOption = currentAirlineProgramSettings.options.find(
				option => option.type === "email_redirect"
			);

			setTwoFactorData(prevState => ({
				...prevState,
				is2FAEnabled: true,
				alert: emailRedirectOption?.actions.find(action => action.type === "alert")?.text,
				button: {
					text: emailRedirectOption?.actions.find(action => action.type === "button")?.text
				},
				helpTextBeforeClickButton: emailRedirectOption?.actions.find(
					action => action.type === "help_text" && action.trigger === "beforeButtonClick"
				)?.text,
				helpTextAfterClickButton: emailRedirectOption?.actions.find(
					action => action.type === "help_text" && action.trigger === "afterButtonClick"
				)?.text,
				modal: {
					title: emailRedirectOption?.actions.find(
						action => action.type === "modal" && action.trigger === "beforeCompletion"
					)?.title,
					description: emailRedirectOption?.actions.find(
						action => action.type === "modal" && action.trigger === "beforeCompletion"
					)?.description,
					timeLeftInSeconds: emailRedirectOption?.actions.find(
						action => action.type === "modal" && action.trigger === "beforeCompletion"
					)?.timeLeftInSeconds,
					maxLength: emailRedirectOption?.actions.find(
						action => action.type === "modal" && action.trigger === "beforeCompletion"
					)?.maxLength
				},
				completionAlert: emailRedirectOption?.actions.find(
					action => action.type === "completion_alert" && action.trigger === "afterCompletion"
				)?.text
			}));
			return;
		}
	}, [currentProgram, airlineProgramsSettings]);

	const values = {
		banks,
		programsFidelity,
		currentProgram,
		setCurrentProgram,
		offerData,
		setOfferData,
		pricesData,
		userData,
		setUserData,
		rankingData,
		setRankingData,
		participantData,
		fetchBanksData,
		fetchDataUser,
		fetchRankingData,
		newOfferTempData,
		setNewOfferTempData,
		bankData,
		setBankData,
		loyaltyData,
		setLoyaltyData,
		stepValidation,
		handlerStepValidation,
		editOffer,
		selectOfferToEdit,
		offersData,
		forceStep,
		setForceStep,
		fetchPricesData,
		smilesRange,
		setSmilesRange,
		fetchCreditCardData,
		creditCardData,
		setCreditCardData,
		postData,
		quickSaleProblem,
		setQuickSaleProblem,
		offerSentStatus,
		fetchOffersData,
		cleanNewOfferTempData,
		positionRank,
		setPositionRank,
		smilesDiamond,
		clearContext,
		fetchParticipantData,
		setStatisticSales,
		fetchStatisticSales,
		statisticSales,
		phoneMultiplus,
		getPhoneMultiplus,
		allPhonesMultiplus,
		fetchBonusData,
		bonusData,
		setBonusData,
		acceptanceTerms,
		setAcceptanceTerms,
		forwardPaymentProblem,
		setForwardPaymentProblem,
		setUuidCybersource,
		uuidCybersource,
		setSmilesDiamond,
		isEditOffer,
		airlineProgramsSettings,
		setAirlineProgramsSettings,
		check2FAEmailRedirectStatus: postAndCheck2FAEmailRedirectStatus,
		email2FAStatusValidation,
		showModalErrorTwoFactorAuthentication,
		setShowModalErrorTwoFactorAuthentication,
		status2FAEnabledFeature,
		twoFactorData,
		setTwoFactorData,
		request2FACodeLatam,
		send2FACode,
		hasClickedRequest2FACode,
		setHasClickedRequest2FACode,
		hasTriedToProceedWithout2FA,
		setHasTriedToProceedWithout2FA
	};

	// Solução temporária pra conseguir atualizar context através do redux, será removido futuramente.
	window.newOfferContext = {
		fetchParticipantData,
		selectOfferToEdit
	};

	return <NewOfferContext.Provider value={values}>{children}</NewOfferContext.Provider>;
};
